import React, { useState } from 'react';
import { ChatSession, UserInfo } from '../services/apiService';
import { useTheme } from '../theme/ThemeContext';

interface ChatSidebarProps {
  currentUser: UserInfo | null;
  chatSessions: ChatSession[];
  activeSessionId: string | null;  // Changed to string for UUID
  sessionToDelete: string | null;  // Changed to string for UUID
  onCreateNewSession: () => void;
  onSwitchToSession: (sessionId: string) => void;  // Changed to string for UUID
  onDeleteSession: (sessionId: string) => void;  // Changed to string for UUID
  onShowDeleteConfirmation: (sessionId: string) => void;  // Changed to string for UUID
  onCancelDelete: () => void;
  onLogout: () => void;
  onCloseMobile?: () => void;
  onOpenThemeSettings?: () => void;
  onOpenAdminPanel?: () => void;
}

export default function ChatSidebar({
  currentUser,
  chatSessions,
  activeSessionId,
  sessionToDelete,
  onCreateNewSession,
  onSwitchToSession,
  onDeleteSession,
  onShowDeleteConfirmation,
  onCancelDelete,
  onLogout,
  onCloseMobile,
  onOpenThemeSettings,
  onOpenAdminPanel
}: ChatSidebarProps) {
  const { mode } = useTheme();

  // Check if user is admin based on email domain
  const isAdmin = currentUser?.username && (
    currentUser.username.endsWith('@lexhr.ai') || 
    currentUser.username.endsWith('@dataexpert.hu')
  );

  return (
    <>
      <div className="w-64 theme-bg-secondary theme-sidebar-text flex flex-col h-full">
        {/* Header */}
        <div className="h-14 border-b theme-border flex items-center justify-between px-4">
          <div className="flex items-center space-x-3">
            <img 
              src={mode === 'dark' ? '/lexdark.webp' : '/lex.webp'} 
              alt="Lex Logo" 
              className="h-6 object-contain"
            />
            {/* <div className="text-lg font-medium theme-sidebar-text">AI Chatbot</div> */}
          </div>
          <div className="flex items-center space-x-2">
            {/* Close button for mobile */}
            {onCloseMobile && (
              <button 
                onClick={onCloseMobile}
                className="lg:hidden theme-sidebar-text-muted hover:theme-sidebar-text p-1 rounded transition-colors"
                title="Close sidebar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {/* Theme Settings Button */}
            <button 
              onClick={() => onOpenThemeSettings?.()}
              className="theme-sidebar-text-muted hover:theme-sidebar-text p-1 rounded transition-colors"
              title="Theme Settings"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* New chat button */}
        <div className="p-1">
          <button 
            onClick={() => {
              onCreateNewSession();
              onCloseMobile?.();
            }}
            className="w-full py-2 px-3 theme-sidebar-hover theme-sidebar-text rounded-lg hover:opacity-80 transition-colors text-sm flex items-center justify-start theme-sidebar-hover"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            New Chat
          </button>
        </div>
        
        {/* Chat sessions list - scrollable */}
        <div className="flex-1 overflow-y-auto px-1 min-h-0 sidebar-scroll">
          <div className="px-2 py-1 text-xs font-bold theme-sidebar-text-muted uppercase tracking-wider sticky top-0 theme-bg-secondary  z-10">
            Chat History
          </div>
          <div className="pb-2">
            {chatSessions.map(session => (
              <div 
                key={session.id}
                onClick={() => {
                  onSwitchToSession(session.id);
                  onCloseMobile?.();
                }}
                className={`px-2 py-0 cursor-pointer theme-sidebar-hover rounded text-sm flex justify-between items-center relative group mb-0.5 ${
                  activeSessionId === session.id ? 'theme-sidebar-hover theme-bg-tertiary' : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className={"font-medium truncate text-xs theme-sidebar-text " + (activeSessionId === session.id ? 'theme-primary-text' : '')}>{session.title}</div>
                </div>
                
                {/* Delete button and confirmation */}
                {sessionToDelete === session.id ? (
                  <div 
                    className="absolute right-0 top-0 bottom-0 flex items-center theme-sidebar-hover rounded-lg px-2 z-10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="text-xs theme-sidebar-text mr-2">Delete?</span>
                    <button 
                      onClick={() => onDeleteSession(session.id)}
                      className="theme-success hover:opacity-80 p-0.5 py-0 rounded-full theme-sidebar-hover"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        onCancelDelete();
                      }}
                      className="theme-sidebar-text-muted hover:theme-sidebar-text p-0.5 py-0 rounded-full theme-sidebar-hover"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowDeleteConfirmation(session.id);
                    }}
                    className="ml-2 theme-sidebar-text-muted theme-error p-1 rounded-full theme-sidebar-hover transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* User info and logout at bottom */}
        <div className="border-t theme-border">
          {/* User info box */}
          <div className="p-3 border-b theme-border">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 theme-bg-tertiary rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 theme-sidebar-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium theme-sidebar-text truncate">{currentUser?.username}</div>
                <div className="text-xs theme-sidebar-text-muted">Signed in</div>
              </div>
            </div>
          </div>
          
          {/* Admin button (only for admins) */}
          {isAdmin && (
            <div className="p-3 py-1 theme-border">
              <button
                onClick={() => {
                  onOpenAdminPanel?.();
                  onCloseMobile?.();
                }}
                className="w-full py-1 px-3 theme-primary text-white text-sm hover:theme-sidebar-text rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Admin Panel</span>
              </button>
            </div>
          )}
          
          {/* Logout button */}
          <div className="p-3">
            <button
              onClick={onLogout}
              className="w-full py-2 px-3 text-sm theme-sidebar-text-muted hover:theme-sidebar-text theme-sidebar-hover rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Sign out</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
